import { useNavigate } from "react-router-dom";
import "./responsiveAfficheProduitClient.css";
import { ArticleInfo } from "../../../Modeles/ArticleModel";
import SidBarClient from "./SideBarClient";
import { useCallback, useEffect, useMemo, useState } from "react";
import DefaulteImage from "../../Admin/IMG/3.png";
import EmptyCartPopup from "../../../components/EmptyCartPopup";
import axios from "axios";
import "./GlobalToast.css";
import "./PremiumButton.css";

interface Product {
  id: string;
  name: string;
  imageUrl: string;
  price: string;
  PrixNetHT?: string;
  quantity: number;
  ref?: any;
  vendeur?: string;
}

type StockState = "in" | "low" | "out";

const LOW_STOCK_THRESHOLD = 5;

const AfficheProduitClient: React.FC<ArticleInfo> = ({
  IdArticle,
  Designation,
  PrixNetHT,
  Description,
  urlImage,
  image,
  stock,
  quantité,
  RefArticle,
  LibelleSubstitut,
  NomMagasin,
  ...props
}) => {
  const navigate = useNavigate();
  const ClientId = localStorage.getItem("ClientId");

  const [value, setValue] = useState(1);
  const [message, setMessage] = useState("");
  const [addingToCart, setAddingToCart] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [showEmptyCartPopup, setShowEmptyCartPopup] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [clientRemise, setClientRemise] = useState<number>(() => {
    const cached = localStorage.getItem("remise");
    return cached ? parseFloat(cached.replace("%", "")) || 0 : 0;
  });
  const [commissionRules, setCommissionRules] = useState<any[]>([]);

  const handleGoBack = () => window.history.back();

  const updateCartCount = useCallback(() => {
    const stored = localStorage.getItem("productsClient");
    if (!stored) {
      setCartCount(0);
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      setCartCount(Array.isArray(parsed) ? parsed.length : 0);
    } catch {
      setCartCount(0);
    }
  }, []);

  useEffect(() => {
    updateCartCount();
  }, [updateCartCount]);

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      setLoading(true);
      try {
        const promises: Promise<any>[] = [];

        // 1. Commission rules
        promises.push(
          axios
            .get(`${process.env.REACT_APP_PHP_APP_URL}/admin/commission`)
            .then((r) => {
              if (active) setCommissionRules(r.data || []);
            })
            .catch(() => {
              if (active) setCommissionRules([]);
            })
        );

        // 2. Client profile for remise
        if (ClientId) {
          promises.push(
            axios
              .get(`${process.env.REACT_APP_PHP_APP_URL}/client/${ClientId}/profile`)
              .then((res) => {
                const c = res.data?.client;
                if (c && active) {
                  const r = parseFloat(c.RemiseGlobal ?? c.RemiseGlobale ?? "0");
                  setClientRemise(isNaN(r) ? 0 : r);
                }
              })
              .catch(() => {})
          );
        }

        await Promise.all(promises);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadData();
    return () => {
      active = false;
    };
  }, [ClientId]);

  const getGlobalCommission = (): number => {
    const globalRule = commissionRules.find((r: any) => r.commission_type === "global");
    return globalRule ? parseFloat(globalRule.commission_value) || 0 : 0;
  };

  const getMagasinCommission = (nomMagasin?: string): number => {
    if (!nomMagasin) return 0;
    const magasinRule = commissionRules.find(
      (r: any) =>
        r.commission_type === "magasin_all" &&
        r.source_magasin_name?.toLowerCase() === nomMagasin?.toLowerCase()
    );
    return magasinRule ? parseFloat(magasinRule.commission_value) || 0 : 0;
  };

  const finalImage = urlImage || image || DefaulteImage;
  const finalNomMag = NomMagasin || "Magasin";

  const pHT = parseFloat(String(PrixNetHT ?? "0").replace(",", "."));
  const commissionWyn = getGlobalCommission();

  // Prix ancien (barré) = prix_original × (1 + commission_globale%) × (1 + remise%)
  // Exemple: 131.52 × 1.03 × 1.05 = 142.23
  const oldPrice = clientRemise > 0
    ? pHT * (1 + commissionWyn / 100) * (1 + clientRemise / 100)
    : pHT * (1 + commissionWyn / 100);

  // Prix final = prix ancien × (1 - remise%)
  // Exemple: 142.23 × 0.95 = 135.12
  const finalPrice = clientRemise > 0
    ? oldPrice * (1 - clientRemise / 100)
    : oldPrice;

  const cleanPriceFormatted = finalPrice.toFixed(2);
  // Show old price crossed-out only when there is a remise
  const showOldPrice = clientRemise > 0 && pHT > 0;
  const discountPercent = clientRemise > 0 ? Math.round(clientRemise) : 0;

  // stock / quantité were previously received but never surfaced in the UI
  const stockState: StockState = useMemo(() => {
    // Robust quantity extraction to handle encoding issues like "Quantit"
    const qtyKey = Object.keys(props).find(k => k.toLowerCase().startsWith('quantit') || k.toLowerCase() === 'qte' || k.toLowerCase() === 'stock');
    const availableQty = qtyKey ? Number(props[qtyKey]) : Number(quantité ?? stock ?? 0);
    
    if (isNaN(availableQty) || availableQty <= 0) return "out";
    if (availableQty <= LOW_STOCK_THRESHOLD) return "low";
    return "in";
  }, [stock, quantité, props]);

  const stockLabel: Record<StockState, string> = {
    in: "En stock",
    low: "Stock limité",
    out: "Rupture de stock",
  };

  const handleQtyChange = (next: number) => {
    setValue(Math.max(1, Number.isNaN(next) ? 1 : next));
  };

  const addProductToPanier = async () => {
    if (stockState === "out" || addingToCart) return;

    if (!localStorage.getItem("StartDate")) {
      localStorage.setItem("StartDate", new Date().toISOString());
    }
    setAddingToCart(true);

    const cleanPrice = parseFloat(String(cleanPriceFormatted).replace(",", "."));
    const finalPrice = isNaN(cleanPrice) ? "0" : cleanPrice.toFixed(2);
    const uniqueId = String(IdArticle) + "_" + finalNomMag;

    try {
      const formData = new FormData();
      formData.append("IdUtilisateur", ClientId || "");
      formData.append("IdClient", ClientId || "");
      formData.append("TypeUtilisiteur", "clients");
      formData.append("IdArticle", String(IdArticle));
      formData.append("RefArticle", RefArticle || String(IdArticle));
      formData.append("Designation", Designation);
      formData.append("Image", finalImage);
      formData.append("price", finalPrice.toString());
      formData.append("quantity", value.toString());
      formData.append("NomMagasin", finalNomMag);
      formData.append("seller", finalNomMag);

      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/panier`, formData);
      localStorage.setItem('cartExpiration', String(Date.now() + 50 * 60 * 1000)); // 50 minutes

      const newProduct: Product = {
        id: uniqueId,
        name: Designation,
        imageUrl: finalImage,
        price: finalPrice,
        PrixNetHT: finalPrice,
        quantity: value,
        ref: RefArticle || String(IdArticle),
        vendeur: finalNomMag,
      };

      let arr: Product[] = [];
      const localStorageData = localStorage.getItem("productsClient");
      if (localStorageData) {
        try {
          const parsed = JSON.parse(localStorageData);
          arr = Array.isArray(parsed) ? parsed : [];
        } catch {
          arr = [];
        }
      }

      const productIndex = arr.findIndex((p) => p.id === uniqueId);
      if (productIndex !== -1) {
        arr[productIndex].quantity = Number(arr[productIndex].quantity) + Number(value);
      } else {
        arr.push(newProduct);
      }

      localStorage.setItem("productsClient", JSON.stringify(arr));
      window.dispatchEvent(new Event("panier-updated"));
      updateCartCount();

      setMessage("Produit ajouté au panier avec succès !");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error adding to cart", err);
    } finally {
      setTimeout(() => setAddingToCart(false), 800);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#FAFAFA' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #F5A623', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <span style={{ marginTop: '16px', color: '#64748B', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Chargement des données...</span>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="apc-wrapper">
      <EmptyCartPopup show={showEmptyCartPopup} onClose={() => setShowEmptyCartPopup(false)} />
      <SidBarClient />

      <main className="apc-main">
        <div className="apc-topbar">
          <button onClick={handleGoBack} className="apc-back-btn" aria-label="Retour à la page précédente">
            <i className="bi bi-arrow-left" />
            <span>Retour</span>
          </button>

          <button
            className="apc-cart-btn"
            onClick={() =>
              cartCount === 0 ? setShowEmptyCartPopup(true) : navigate(`/client/${ClientId}/panier`)
            }
          >
            <i className="bi bi-cart3" />
            <span>Mon panier</span>
            {cartCount > 0 && <span className="apc-cart-badge">{cartCount}</span>}
          </button>
        </div>

        {/* Global Toast Container */}
        <div className="cl-toast-container">
          {message && (
            <div className="cl-toast cl-toast--success" role="alert">
              <i className="bi bi-check-circle-fill" />
              <span className="cl-toast-msg">{message}</span>
            </div>
          )}
        </div>

        <div className="apc-card">
          <div className="apc-grid">
            {/* Media */}
            <div className="apc-media">
              <span className="apc-media-tag">FIG. 01</span>
              <div className="apc-media-frame">
                <span className="apc-corner apc-corner--tl" aria-hidden="true" />
                <span className="apc-corner apc-corner--tr" aria-hidden="true" />
                <span className="apc-corner apc-corner--bl" aria-hidden="true" />
                <span className="apc-corner apc-corner--br" aria-hidden="true" />
                <img src={finalImage} alt={Designation} className="apc-media-img" />
              </div>
              <div className={`apc-stock apc-stock--${stockState}`}>
                <span className="apc-stock-dot" />
                {stockLabel[stockState]}
              </div>
            </div>

            {/* Info */}
            <div className="apc-info">
              <div className="apc-eyebrow-row">
                <span className="apc-ref">RÉF. {RefArticle}</span>
                {NomMagasin && <span className="apc-seller">{NomMagasin}</span>}
              </div>

              <h1 className="apc-title">{Designation}</h1>

              <div className="apc-verified">
                <i className="bi bi-patch-check-fill" />
                Pièce contrôlée WYN
              </div>

              {LibelleSubstitut && (
                <div className="apc-substitut">
                  <i className="bi bi-arrow-left-right" />
                  Équivalent : <strong>{LibelleSubstitut}</strong>
                </div>
              )}

              <div className="apc-price-block">
                <div className="apc-price-row">
                  <span className="apc-price">{cleanPriceFormatted}</span>
                  <span className="apc-price-unit">MAD TTC / unité</span>
                </div>
                {showOldPrice && (
                  <div className="apc-price-old-row">
                    <span className="apc-price-old">{oldPrice.toFixed(2)} MAD</span>
                    <span className="apc-discount-badge">-{discountPercent}%</span>
                  </div>
                )}
              </div>

              {Description && (
                <div className="apc-desc">
                  <div className="apc-desc-head">
                    <i className="bi bi-file-earmark-text" />
                    Fiche technique
                  </div>
                  <p>{Description}</p>
                </div>
              )}

              <div className="apc-buy">
                <div className="apc-qty">
                  <span className="apc-qty-label">Quantité</span>
                  <div className="apc-stepper">
                    <button
                      type="button"
                      className="apc-stepper-btn"
                      onClick={() => handleQtyChange(value - 1)}
                      aria-label="Diminuer la quantité"
                    >
                      –
                    </button>
                    <input
                      className="apc-stepper-input"
                      type="number"
                      min={1}
                      value={value}
                      onChange={(e) => handleQtyChange(parseInt(e.target.value, 10))}
                    />
                    <button
                      type="button"
                      className="apc-stepper-btn"
                      onClick={() => handleQtyChange(value + 1)}
                      aria-label="Augmenter la quantité"
                    >
                      +
                    </button>
                  </div>
                </div>

                <button
                  className="apc-add-btn"
                  disabled={addingToCart || stockState === "out"}
                  onClick={addProductToPanier}
                >
                  {addingToCart ? (
                    <>
                      <span className="apc-spinner" /> Ajout...
                    </>
                  ) : stockState === "out" ? (
                    "Indisponible"
                  ) : (
                    <>
                      <i className="bi bi-cart-plus-fill" /> Ajouter au panier
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AfficheProduitClient;